import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Clipboard2HeartFill, EyeFill, Flag, FlagFill, Link, PinAngleFill, PinFill, PlusCircleDotted } from 'react-bootstrap-icons'
import { NavLink, useParams } from 'react-router-dom'
import Authentication from '../../../../libs/api/services/Authentication'
import Patient from '../../../../libs/api/services/Patient'
import QuestionnaireAssignment from '../../../../libs/api/services/QuestionnaireAssignment'
import { ClaimsFilter } from '../../../../libs/authentication/ClaimsFilter'
import CommonFunctionsContext from '../../../common/CommonFunctionsProvider'
import PatientContext from '../../patient/management/PatientProvider'
import QuestionnaireAssignmentAdmin from '../assignment/QuestionnaireAssignmentAdmin'

export default function PatientQuestionnaires() {
	const { id } = useParams()
	const { setSectionInfo } = useContext(CommonFunctionsContext)
	const { activePatient } = useContext(PatientContext)
	const [mode, setMode] = useState('')
	const [patient, setPatient] = useState()
	const [questionnaires, setQestionnaires] = useState([])
	const [loading, setLoading] = useState(true)


	const [filters, setFilters] = useState({
		name: undefined,
		assigned: undefined,
		complete: undefined,
	})

	const [sort, setSort] = useState({ field: 'assigned', dir: 'desc' })

	const _handleOnAdd = (v) => {
		load()
		setTimeout(setMode, 300, '')
	}

	const _setupAdd = () => {
		Patient.GetById(id)
			.then((r) => {
				setPatient(r)
				setMode('Add')
			})

	}

	useEffect(() => {
		if (activePatient)
			setSectionInfo(`Questionnaires for ${activePatient.userProfile.firstName} ${activePatient.userProfile.lastName} at ${Authentication.getFacilityName()}`);
		else
			setSectionInfo(`Questionnaires`);
	}, [activePatient, setSectionInfo])

	const _checkPinned = (id) => {

		try {
			var quest = questionnaires.find(r => r.id === id)
			const isPinned = JSON.parse(quest.options.find(r => r.property === "PIN_TO_DASH").value)
			if (isPinned)
				return <button onClick={(e) => _handlePin(id, false)} className='btn btn-xs w-100 text-start text-primary'><PinFill />Unpin from patient dashboard </button>
		} catch (e) { }

		return <button onClick={(e) => _handlePin(id, true)} className='btn btn-xs w-100 text-start text-secondary'><PinAngleFill />Pin to patient dashboard </button>
	}

	const load = React.useCallback(() => {
		// Omit name filter when fetching so we can perform fuzzy/leet matching client-side
		const requestFilters = { ...filters }
		if (requestFilters && requestFilters.name) requestFilters.name = undefined
		QuestionnaireAssignment
			.GetPatientQuestionnairesByType(id, 'Questionnaire', requestFilters)
			.then((r) => {
				setQestionnaires(r)
				setLoading(false)
			})
	}, [id, filters])

	useEffect(() => {
		load()
	}, [filters, load])

	const listRefs = useRef([])
	const nameSearchRef = useRef(null)
	const [selectedIdx, setSelectedIdx] = useState(-1)

	const sortedQuestionnaires = React.useMemo(() => {
		if (!questionnaires || questionnaires.length === 0) return []
		const items = [...questionnaires]
		const dir = sort.dir === 'asc' ? 1 : -1
		items.sort((a, b) => {
			if (!a.resource && !b.resource) return 0
			if (!a.resource) return 1
			if (!b.resource) return -1
			switch (sort.field) {
				case 'name':
					return dir * ((a.resource.questionnaire.name || '').localeCompare(b.resource.questionnaire.name || ''))
				case 'assigned':
					// Treat missing dates as last always
					if (!a.resource.assignedDate && !b.resource.assignedDate) return 0
					if (!a.resource.assignedDate) return 1
					if (!b.resource.assignedDate) return -1
					const aAssigned = new Date(a.resource.assignedDate).getTime()
					const bAssigned = new Date(b.resource.assignedDate).getTime()
					return dir * (aAssigned - bAssigned)
				case 'completed':
					if (!a.resource.completedDate && !b.resource.completedDate) return 0
					if (!a.resource.completedDate) return 1
					if (!b.resource.completedDate) return -1
					const aCompleted = new Date(a.resource.completedDate).getTime()
					const bCompleted = new Date(b.resource.completedDate).getTime()
					return dir * (aCompleted - bCompleted)
				case 'alerts':
					const aAlerts = a.resource.alerts ? a.resource.alerts.length : 0
					const bAlerts = b.resource.alerts ? b.resource.alerts.length : 0
					return dir * (aAlerts - bAlerts)
				default:
					return 0
			}
		})

		// If name filter present, perform client-side fuzzy matching and scoring
		const normalize = (v) => {
			if (!v) return ''
			const map = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '2': 'z', '8': 'b', '6': 'g', '9': 'g' }
			return v.toString().toLowerCase().split('').map(c => map[c] || c).join('').replace(/[^a-z0-9]/g, '')
		}

		const scoreMatch = (name, term) => {
			if (!term || term.length === 0) return 0
			const s = normalize(name)
			const t = normalize(term)
			if (!s || !t) return 0
			if (s === t) return 1000
			if (s.includes(t)) return 800 + (s.indexOf(t) === 0 ? 100 : 0) - t.length
			// subsequence
			let si = 0, ti = 0
			while (si < s.length && ti < t.length) {
				if (s[si] === t[ti]) ti++
				si++
			}
			if (ti === t.length) return 500 + t.length
			const common = t.split('').filter(c => s.indexOf(c) !== -1).length
			return common > 0 ? 100 + common : 0
		}

		if (filters && filters.name) {
			const term = filters.name
			const scoredRaw = items.map(i => ({ item: i, score: i.resource && i.resource.questionnaire ? scoreMatch(i.resource.questionnaire.name || '', term) : 0 }))
			const scored = scoredRaw.filter(x => x.score > 0).sort((a, b) => b.score - a.score).map(x => x.item)
			return scored
		}

		return items
	}, [questionnaires, sort, filters])

	useEffect(() => {
		// trim refs array to match items
		listRefs.current = listRefs.current.slice(0, sortedQuestionnaires.length)
	}, [sortedQuestionnaires])

	const handleListKeyDown = (e) => {
		if (e.key === 'Escape') {
			setSelectedIdx(-1)
			if (nameSearchRef.current && typeof nameSearchRef.current.focus === 'function') {
				nameSearchRef.current.focus()
				e.preventDefault()
			}
			return;
		}

		// Activate item on Enter/Space
		if (e.key === 'Enter' || e.key === ' ') {
			const items = listRefs.current
			const active = document.activeElement
			let idx = items.findIndex(x => x && (x === active || (typeof x.contains === 'function' && x.contains(active))))
			if (idx === -1) idx = selectedIdx
			if (idx >= 0 && items[idx]) {
				try {
					const target = items[idx].querySelector && items[idx].querySelector('a, button:not([style*="-9999px"])')
					if (target && typeof target.click === 'function') {
						target.click()
						e.preventDefault()
					}
				} catch (e) { }
			}
			return
		}
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
		const items = listRefs.current
		if (!items || items.length === 0) return
		const active = document.activeElement
		const idx = items.findIndex(x => x && (x === active || (typeof x.contains === 'function' && x.contains(active))))
		let nextIdx = -1
		if (idx === -1) {
			// focus first (down) or last (up)
			nextIdx = e.key === 'ArrowDown' ? 0 : items.length - 1
		} else {
			nextIdx = e.key === 'ArrowDown' ? Math.min(items.length - 1, idx + 1) : Math.max(0, idx - 1)
		}
		const next = items[nextIdx]
		if (next) {
			setSelectedIdx(nextIdx)
			let focusTarget = next
			// Prefer a visible anchor/button inside the row if present
			try {
				const visible = next.querySelector && next.querySelector('a, button:not([style*="-9999px"])')
				if (visible) focusTarget = visible
			} catch (e) { }
			if (focusTarget && typeof focusTarget.focus === 'function') {
				focusTarget.focus()
				try { focusTarget.scrollIntoView({ block: 'nearest' }) } catch (e) { }
				e.preventDefault()
			}
		}
	}

	useEffect(() => {
		// trim refs array to match items
		listRefs.current = listRefs.current.slice(0, sortedQuestionnaires.length)
	}, [sortedQuestionnaires])

	const _handleSort = (field) => {
		if (sort.field === field)
			setSort({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
		else
			setSort({ field: field, dir: 'asc' })
	}

	const _handlePin = (qid, val) => {
		QuestionnaireAssignment
			.SetAssignmentOption(qid, 'PIN_TO_DASH', val)
			.then((r) => {
				load()
			})
	}

	if (mode === "Add" && patient)
		return (<QuestionnaireAssignmentAdmin
			onDone={(v) => _handleOnAdd(v)}
			onCancel={(e) => setMode('')}
			skip={[2]}
			startAt={1}
			defaultValues={{ user: patient }}
		/>)
	else
		return (
			<div className='d-flex flex-column h-100' onKeyDown={handleListKeyDown}>
				<ClaimsFilter resources={[{ resource: "Questionnaires", type: 'Inherit-Edit' }, { resource: "Questionnaires", type: 'Edit' }]} claim={[]}>
					<div className='d-flex flex-row text-center p-3 bg-secondary-container flex-shrink-1'>

						<div
							onClick={(e) => _setupAdd()}
							style={{ cursor: 'pointer' }} className=' bg-primary-container m-2 p-2 rounded-3'>
							<PlusCircleDotted
							/> Assign a questionnaire
						</div>
						{/* <div style={{ cursor: 'pointer' }} className=' bg-primary-container m-2 p-2 shadow '>
						Assigned (100)
					</div>
					<div style={{ cursor: 'pointer' }} className=' bg-secondary-container m-2 p-2'>
						Complete (99)
					</div>
					<div style={{ cursor: 'pointer' }} className=' bg-secondary-container m-2 p-2'>
						Incomplete (1)
					</div> */}
					</div>
				</ClaimsFilter>
				<div className='row text-center mb-2 bg-primary-container flex-shrink-1' >
					<div className='col-1' style={{ cursor: 'pointer' }} onClick={() => _handleSort('alerts')}>
						<div>Alerts {sort.field === 'alerts' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
					</div>
					<div className='col-3 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => _handleSort('name')}>
						<div><b>Name</b> {sort.field === 'name' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
						<input ref={nameSearchRef} autoFocus onChange={e => setFilters({ ...filters, name: e.target.value === '' ? undefined : e.target.value })} className='form-control form-control-sm m-2' placeholder='Search' />
					</div>
					<div className='col-3 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => _handleSort('assigned')}>
						<div><b>Assigned On</b> {sort.field === 'assigned' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
						<input onChange={e => setFilters({ ...filters, assigned: !e.target.value ? undefined : e.target.value.toString() })} type='date' className='form-control form-control-sm m-2' placeholder='Search' />
					</div>
					<div className='col-3 d-flex flex-column' style={{ cursor: 'pointer' }} onClick={() => _handleSort('completed')}>
						<div><b>Completed On</b> {sort.field === 'completed' ? (sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown />) : ''}</div>
						<input onChange={e => setFilters({ ...filters, complete: !e.target.value ? undefined : e.target.value.toString() })} type='date' className='form-control form-control-sm m-2' placeholder='Search' />
					</div>
					<div className='col-2'>
						<b>Options</b>
					</div>
				</div>
				<div style={{ overflow: 'auto', position: 'relative' }} className=' flex-grow-1'>
					<div className={'justify-content-center align-items-center position-absolute bg-primary-container '.concat(loading ? 'show-loading' : 'hide-loading')} style={{ left: '0px', top: '0px', right: '0px', bottom: '0px', zIndex: 1000 }}>
						<div className='text-center' >

							{/* <div>{itemsLoaded}/{totalItems}</div> */}
							<Clipboard2HeartFill size={75} />
							<div>
								<FontAwesomeIcon className='me-2' size='2x' icon={faSpinner} spinPulse />
								Loading patient questionnaires
							</div>
						</div>
					</div>
					<div tabIndex={0} style={{ outline: 'none' }}>
						{
							sortedQuestionnaires.map((q, idx) => q.resource &&
								<div key={q.id} ref={(el) => listRefs.current[idx] = el} tabIndex={-1} onFocus={() => setSelectedIdx(idx)} className={'row mb-1 text-center'.concat(selectedIdx === idx ? ' bg-light' : '')} style={selectedIdx === idx ? { backgroundColor: '#eaf6ff' } : {}}>
									<div className='col-1'>
										{
											q.resource.alerts.length > 0
												?
												<FlagFill className='text-danger me-2' />
												:
												<Flag className='text-secondary me-2' />
										}

									</div>
									<div className='col-3 text-start'>

										{q.resource.questionnaire.name}
									</div>
									<div className='col-3'>
										{q.resource.assignedDate.substring(0, 19)}
									</div>
									<div className='col-3'>
										{q.resource.completedDate ? q.resource.completedDate.substring(0, 19) : ''}
									</div>
									<div className='col-2 text-center d-flex flex-column align-items-start '>
										<NavLink
											className={'btn btn-sm w-100 text-start text-primary'} tag={Link}
											// to={menuItems[i].location}
											to={'/Dashboard/Patient/' + id + '/Questionanire/' + q.resource.questionnaire.id + "/" + q.resource.id}
										>
											<EyeFill />View Questionnaire
										</NavLink>
										{/* keep an off-screen helper button as a fallback focus target (no ref here to avoid clobbering refs to the visible control) */}
										<button
											style={{ position: 'absolute', left: '-9999px' }}
											aria-hidden='true'
											aria-label={`Focus target ${q.resource.questionnaire.name}`}
											onFocus={() => setSelectedIdx(idx)}
										/>
										{_checkPinned(q.id)}
									</div>
								</div>
							)
						}
					</div>
				</div>
			</div>
		)
}
